import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { canAdminPlans } from "@/lib/permissions";
import { toast } from "sonner";
import {
  ArrowLeft, Save, Plus, Trash2, Upload, AlertTriangle,
  CheckCircle2, MapPin, Compass, Ruler, ChevronRight, ChevronLeft,
  FileText, X, Eye, EyeOff, Layers, Crosshair, Pencil,
} from "lucide-react";
import ReadinessBanner from "@/components/ReadinessBanner";

// SC-1 — Project Survey Control surface.
// Single page that hosts:
//   1. Status card (RED / AMBER / GREEN)
//   2. First-time setup wizard (auto-opens when settings.configured===false)
//   3. Settings panel (datum / coord system / units / vertical / rotation)
//   4. Control Points (list + add + CSV import)
//   5. Benchmarks (list + add)
//
// PLAIN-ENGLISH RULE: No EPSG codes, no projection strings exposed in UI.
// All options come from /api/survey-control/catalog (server-owned labels).
//
// Permissions: read for everyone; write only for owner / executive / admin /
// project_manager (matched via canAdminPlans on the frontend; backend is
// the authoritative gate).

const STATE_COLORS = {
  red:   { bg: "rgba(239,68,68,0.10)",  border: "rgba(239,68,68,0.55)",  text: "rgb(239,68,68)",  pill: "RED" },
  amber: { bg: "rgba(245,158,11,0.10)", border: "rgba(245,158,11,0.55)", text: "rgb(245,158,11)", pill: "AMBER" },
  green: { bg: "rgba(34,197,94,0.10)",  border: "rgba(34,197,94,0.55)",  text: "rgb(34,197,94)",  pill: "GREEN" },
};

export default function SurveyControl() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = canAdminPlans(user);

  const [project, setProject] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [settings, setSettings] = useState(null);
  const [status, setStatus] = useState(null);
  const [controlPoints, setControlPoints] = useState([]);
  const [benchmarks, setBenchmarks] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modals
  const [wizardOpen, setWizardOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [newCPOpen, setNewCPOpen] = useState(false);
  const [newBMOpen, setNewBMOpen] = useState(false);
  // Iter FIELD-UX-1 — edit modals for manual entries
  const [editCP, setEditCP] = useState(null);   // null | controlPoint row
  const [editBM, setEditBM] = useState(null);   // null | benchmark row

  const reloadAll = async () => {
    const [p, cat, s, st, cp, bm] = await Promise.all([
      api.get(`/projects/${projectId}`).then(r => r.data).catch(() => null),
      api.get(`/survey-control/catalog`).then(r => r.data).catch(() => null),
      api.get(`/projects/${projectId}/survey-control/settings`).then(r => r.data).catch(() => null),
      api.get(`/projects/${projectId}/survey-control/status`).then(r => r.data).catch(() => null),
      api.get(`/projects/${projectId}/survey-control/control-points`).then(r => r.data).catch(() => []),
      api.get(`/projects/${projectId}/survey-control/benchmarks`).then(r => r.data).catch(() => []),
    ]);
    setProject(p); setCatalog(cat); setSettings(s); setStatus(st);
    setControlPoints(cp || []); setBenchmarks(bm || []);
    setLoading(false);
    // Iter FIELD-UX-1 — only auto-open the wizard the FIRST time per
    // (user, project). Operators reported the wizard re-opening on
    // every visit and intercepting clicks on manual-entry buttons.
    // Once dismissed, respect that choice until reset.
    const dismissKey = `fop:sc-wizard-dismissed:${projectId}`;
    const alreadyDismissed = typeof window !== "undefined" && window.localStorage?.getItem(dismissKey);
    if (s && !s.configured && canWrite && !alreadyDismissed) setWizardOpen(true);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [p, cat, s, st, cp, bm] = await Promise.all([
        api.get(`/projects/${projectId}`).then(r => r.data).catch(() => null),
        api.get(`/survey-control/catalog`).then(r => r.data).catch(() => null),
        api.get(`/projects/${projectId}/survey-control/settings`).then(r => r.data).catch(() => null),
        api.get(`/projects/${projectId}/survey-control/status`).then(r => r.data).catch(() => null),
        api.get(`/projects/${projectId}/survey-control/control-points`).then(r => r.data).catch(() => []),
        api.get(`/projects/${projectId}/survey-control/benchmarks`).then(r => r.data).catch(() => []),
      ]);
      if (cancelled) return;
      setProject(p); setCatalog(cat); setSettings(s); setStatus(st);
      setControlPoints(cp || []); setBenchmarks(bm || []);
      setLoading(false);
      // Iter FIELD-UX-1 — respect persisted dismissal (see reloadAll).
      const dismissKey = `fop:sc-wizard-dismissed:${projectId}`;
      const alreadyDismissed = typeof window !== "undefined" && window.localStorage?.getItem(dismissKey);
      if (s && !s.configured && canWrite && !alreadyDismissed) setWizardOpen(true);
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  if (loading || !project) {
    return <div className="p-8 font-mono text-xs text-[var(--fop-text-3)]">Loading Survey Control…</div>;
  }

  const stateColor = STATE_COLORS[status?.overall_state || "red"];

  return (
    <div className="p-6 max-w-[1600px] mx-auto" data-testid="survey-control-page">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <Link to={`/project/${projectId}`} className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-[var(--fop-text-3)] hover:text-[var(--fop-text)]" data-testid="back-to-project">
          <ArrowLeft size={12} /> {project.name}
        </Link>
        {canWrite && (
          <div className="flex items-center gap-2">
            <button onClick={() => setWizardOpen(true)} className="btn btn-ghost text-xs inline-flex items-center gap-1.5" data-testid="open-survey-wizard">
              <Compass size={13} /> Setup Wizard
            </button>
          </div>
        )}
      </div>

      <div className="mb-6">
        <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-[var(--fop-text-3)]">Project</div>
        <h1 className="font-display text-3xl text-[var(--fop-text)] leading-tight">Survey Control</h1>
        <p className="text-sm text-[var(--fop-text-2)] mt-1 max-w-2xl">
          Project coordinate system, survey control points, and benchmarks. This is the
          foundation for real-world positioning on plans &mdash; not the per-sheet GPS
          orientation in the viewer.
        </p>
      </div>

      {/* Iter EXP-1 — single READY / NOT READY verdict at top.
          Audit Tier-S #9: 8 sections, no clear answer to "is this project
          field-ready?" Now the first thing a PM sees is the verdict and
          the missing items, link-able directly. */}
      <ReadinessVerdict status={status} settings={settings}
                        controlPoints={controlPoints} benchmarks={benchmarks}
                        canWrite={canWrite}
                        onOpenWizard={() => setWizardOpen(true)} />

      {/* STATUS CARD */}
      <StatusCard status={status} stateColor={stateColor} settings={settings}
                    onOpenWizard={() => setWizardOpen(true)} canWrite={canWrite} />

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4 mt-4">
        <div className="space-y-4">
          {/* SETTINGS */}
          <SettingsPanel
            settings={settings} catalog={catalog} canWrite={canWrite}
            onSaved={reloadAll}
            projectId={projectId}
          />

          {/* CONTROL POINTS */}
          <ControlPointsPanel
            projectId={projectId}
            controlPoints={controlPoints}
            canWrite={canWrite}
            onAdd={() => setNewCPOpen(true)}
            onImport={() => setImportOpen(true)}
            onChanged={reloadAll}
            onEdit={(cp) => setEditCP(cp)}
            unitsLabel={settings?.labels?.horizontal_units}
          />

          {/* BENCHMARKS */}
          <BenchmarksPanel
            projectId={projectId}
            benchmarks={benchmarks}
            canWrite={canWrite}
            onAdd={() => setNewBMOpen(true)}
            onChanged={reloadAll}
            onEdit={(bm) => setEditBM(bm)}
            unitsLabel={settings?.labels?.horizontal_units}
          />

          {/* SC-3 — Sheet Calibrations roll-up */}
          <SheetCalibrationsPanel
            projectId={projectId}
            unitsLabel={settings?.labels?.horizontal_units}
          />
        </div>

        {/* RIGHT RAIL — guidance + glossary (plain English) */}
        <SidebarGuide canWrite={canWrite} />
      </div>

      {wizardOpen && (
        <SetupWizard
          settings={settings} catalog={catalog}
          projectId={projectId}
          onClose={() => {
            // Iter FIELD-UX-1 — remember dismissal so the wizard does
            // not re-open on every visit and block manual-entry CTAs.
            try {
              window.localStorage.setItem(`fop:sc-wizard-dismissed:${projectId}`, String(Date.now()));
            } catch { /* ignore quota */ }
            setWizardOpen(false);
          }}
          onComplete={() => {
            try {
              window.localStorage.setItem(`fop:sc-wizard-dismissed:${projectId}`, String(Date.now()));
            } catch { /* ignore quota */ }
            setWizardOpen(false); reloadAll();
          }}
          navigate={navigate}
        />
      )}
      {importOpen && (
        <ImportCSVModal
          projectId={projectId}
          onClose={() => setImportOpen(false)}
          onCommitted={() => { setImportOpen(false); reloadAll(); }}
        />
      )}
      {newCPOpen && (
        <NewControlPointModal projectId={projectId}
          unitsLabel={settings?.labels?.horizontal_units}
          onClose={() => setNewCPOpen(false)}
          onSaved={() => { setNewCPOpen(false); reloadAll(); }} />
      )}
      {newBMOpen && (
        <NewBenchmarkModal projectId={projectId}
          unitsLabel={settings?.labels?.horizontal_units}
          onClose={() => setNewBMOpen(false)}
          onSaved={() => { setNewBMOpen(false); reloadAll(); }} />
      )}
      {editCP && (
        <NewControlPointModal projectId={projectId}
          unitsLabel={settings?.labels?.horizontal_units}
          initial={editCP}
          onClose={() => setEditCP(null)}
          onSaved={() => { setEditCP(null); reloadAll(); }} />
      )}
      {editBM && (
        <NewBenchmarkModal projectId={projectId}
          unitsLabel={settings?.labels?.horizontal_units}
          initial={editBM}
          onClose={() => setEditBM(null)}
          onSaved={() => { setEditBM(null); reloadAll(); }} />
      )}
    </div>
  );
}


function StatusCard({ status, stateColor, settings, onOpenWizard, canWrite }) {
  if (!status) return null;
  const checks = [
    { ok: status.coordinate_system_configured, label: "Coordinate System",
      value: status.coordinate_system_label || "Not configured" },
    { ok: status.control_points_count > 0, label: "Control Points",
      value: status.control_points_count > 0 ? `${status.control_points_count} imported` : "None imported" },
    { ok: status.benchmarks_count > 0, label: "Benchmarks",
      value: status.benchmarks_count > 0 ? `${status.benchmarks_count} defined` : "None defined" },
    { ok: status.sheets_total === 0 ? null : status.sheets_calibrated > 0,
      label: "Sheets Calibrated",
      value: status.sheets_total === 0
        ? "No sheets in this project yet"
        : `${status.sheets_calibrated} of ${status.sheets_total} sheets` },
  ];
  return (
    <div
      className="rounded-lg p-5 border-2"
      style={{ background: stateColor.bg, borderColor: stateColor.border }}
      data-testid="survey-control-status-card"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span
              className="inline-block w-2.5 h-2.5 rounded-full"
              style={{ background: stateColor.text }}
              data-testid="status-dot"
            />
            <div className="font-mono text-[10px] uppercase tracking-[0.25em]"
                  style={{ color: stateColor.text }}
                  data-testid="status-pill">
              {stateColor.pill}
            </div>
          </div>
          <h2 className="font-display text-2xl text-[var(--fop-text)]">
            Field Positioning: {status.field_positioning_ready ? "READY" : "NOT READY"}
          </h2>
          <p className="text-xs text-[var(--fop-text-3)] mt-1">
            Project survey control readiness, computed live from your settings and uploaded data.
          </p>
        </div>
        {!status.field_positioning_ready && canWrite && (
          <button onClick={onOpenWizard} className="btn btn-primary text-sm inline-flex items-center gap-1.5" data-testid="status-card-cta">
            <Compass size={14} /> Run Setup Wizard
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-5">
        {checks.map((c, i) => (
          <div key={i} className="rounded border border-[var(--fop-border)] bg-[var(--fop-surface)] p-3"
                data-testid={`status-check-${i}`}>
            <div className="flex items-center gap-1.5 mb-1">
              {c.ok === true && <CheckCircle2 size={12} className="text-[rgb(34,197,94)]" />}
              {c.ok === false && <AlertTriangle size={12} className="text-[rgb(239,68,68)]" />}
              {c.ok === null && <span className="w-3 h-3 inline-block rounded-full border border-[var(--fop-border)]" />}
              <div className="font-mono text-[9px] uppercase tracking-wider text-[var(--fop-text-3)]">{c.label}</div>
            </div>
            <div className="text-xs text-[var(--fop-text)] truncate">{c.value}</div>
          </div>
        ))}
      </div>

      {status.missing && status.missing.length > 0 && (
        <div className="mt-4 text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]" data-testid="status-missing">
          <span style={{ color: stateColor.text }}>Missing:</span>{" "}
          {status.missing.join(" · ")}
        </div>
      )}
    </div>
  );
}


function SettingsPanel({ settings, catalog, canWrite, onSaved, projectId }) {
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);

  useEffect(() => { if (settings) setDraft(settings); }, [settings]);

  if (!draft || !catalog) return null;

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        horizontal_datum: draft.horizontal_datum,
        custom_horizontal_datum_name: draft.horizontal_datum === "custom" ? draft.custom_horizontal_datum_name : null,
        coordinate_system_family: draft.coordinate_system_family,
        coordinate_system_zone: ["custom","local_grid"].includes(draft.coordinate_system_family) ? null : draft.coordinate_system_zone,
        custom_coordinate_system_name: draft.coordinate_system_family === "custom" ? draft.custom_coordinate_system_name : null,
        horizontal_units: draft.horizontal_units,
        vertical_datum: draft.vertical_datum,
        custom_vertical_datum_name: draft.vertical_datum === "custom" ? draft.custom_vertical_datum_name : null,
        grid_north_mode: draft.grid_north_mode,
        grid_north_rotation_deg: Number(draft.grid_north_rotation_deg || 0),
        notes: draft.notes || null,
        configured: true,
      };
      await api.put(`/projects/${projectId}/survey-control/settings`, payload);
      toast.success("Survey settings saved");
      setEditing(false);
      onSaved();
    } catch (e) {
      toast.error("Failed to save: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const setF = (k, v) => setDraft(d => ({ ...d, [k]: v }));

  return (
    <div className="fop-surface-2 rounded-lg p-5" data-testid="survey-settings-panel">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-3)]">Section 1</div>
          <h2 className="font-display text-xl text-[var(--fop-text)]">Coordinate System & Units</h2>
        </div>
        {canWrite && !editing && (
          <button onClick={() => setEditing(true)} className="btn btn-ghost text-xs" data-testid="settings-edit-btn">Edit</button>
        )}
        {canWrite && editing && (
          <div className="flex gap-2">
            <button onClick={() => { setDraft(settings); setEditing(false); }} className="btn btn-ghost text-xs" data-testid="settings-cancel-btn">Cancel</button>
            <button onClick={save} disabled={saving} className="btn btn-primary text-xs inline-flex items-center gap-1.5" data-testid="settings-save-btn">
              <Save size={12} /> {saving ? "Saving…" : "Save"}
            </button>
          </div>
        )}
      </div>

      {!editing && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm" data-testid="settings-summary">
          <Row k="Horizontal Datum" v={lookupLabel(catalog.horizontal_datums, draft.horizontal_datum) + (draft.horizontal_datum === "custom" ? ` (${draft.custom_horizontal_datum_name || "—"})` : "")} />
          <Row k="Coordinate System" v={
            draft.coordinate_system_family === "custom"
              ? `Custom · ${draft.custom_coordinate_system_name || "—"}`
              : draft.coordinate_system_family === "local_grid"
                ? "Local Grid"
                : (lookupLabel(catalog.coordinate_system_zones, draft.coordinate_system_zone) || "—")
          } />
          <Row k="Horizontal Units" v={lookupLabel(catalog.horizontal_units, draft.horizontal_units)} />
          <Row k="Vertical Datum" v={lookupLabel(catalog.vertical_datums, draft.vertical_datum) + (draft.vertical_datum === "custom" ? ` (${draft.custom_vertical_datum_name || "—"})` : "")} />
          <Row k="Grid North" v={lookupLabel(catalog.grid_north_modes, draft.grid_north_mode)} />
          <Row k="Rotation" v={`${Number(draft.grid_north_rotation_deg || 0).toFixed(3)}°`} />
          {!draft.configured && (
            <div className="sm:col-span-2 text-[11px] font-mono uppercase tracking-wider text-[rgb(245,158,11)]" data-testid="defaults-warning">
              These are DEFAULTS. Click Edit to confirm or change for this project.
            </div>
          )}
        </div>
      )}

      {editing && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <SelectField label="Horizontal Datum" value={draft.horizontal_datum} options={catalog.horizontal_datums} onChange={v => setF("horizontal_datum", v)} testid="field-horizontal-datum" />
          {draft.horizontal_datum === "custom" && (
            <TextField label="Custom Datum Name" value={draft.custom_horizontal_datum_name} onChange={v => setF("custom_horizontal_datum_name", v)} testid="field-custom-datum-name" />
          )}
          <SelectField label="Coordinate System Family" value={draft.coordinate_system_family} options={catalog.coordinate_system_families} onChange={v => setF("coordinate_system_family", v)} testid="field-cs-family" />
          {!["custom","local_grid"].includes(draft.coordinate_system_family) && (
            <SelectField label="Zone" value={draft.coordinate_system_zone || ""} options={catalog.coordinate_system_zones} onChange={v => setF("coordinate_system_zone", v)} testid="field-cs-zone" />
          )}
          {draft.coordinate_system_family === "custom" && (
            <TextField label="Custom Coordinate System Name" value={draft.custom_coordinate_system_name} onChange={v => setF("custom_coordinate_system_name", v)} testid="field-custom-cs-name" />
          )}
          <SelectField label="Horizontal Units" value={draft.horizontal_units} options={catalog.horizontal_units} onChange={v => setF("horizontal_units", v)} testid="field-units" />
          <SelectField label="Vertical Datum" value={draft.vertical_datum} options={catalog.vertical_datums} onChange={v => setF("vertical_datum", v)} testid="field-vertical-datum" />
          {draft.vertical_datum === "custom" && (
            <TextField label="Custom Vertical Datum Name" value={draft.custom_vertical_datum_name} onChange={v => setF("custom_vertical_datum_name", v)} testid="field-custom-vd-name" />
          )}
          <SelectField label="Grid North" value={draft.grid_north_mode} options={catalog.grid_north_modes} onChange={v => setF("grid_north_mode", v)} testid="field-grid-north" />
          <TextField label="Rotation (degrees)" value={draft.grid_north_rotation_deg ?? 0} inputMode="decimal" onChange={v => setF("grid_north_rotation_deg", v)} testid="field-rotation" />
          <TextField label="Notes (optional)" value={draft.notes || ""} multiline onChange={v => setF("notes", v)} testid="field-notes" cols={2} />
        </div>
      )}
    </div>
  );
}

function lookupLabel(arr, key) {
  if (!arr || !key) return "—";
  const f = arr.find(x => x.key === key);
  return f ? f.label : key;
}

function Row({ k, v }) {
  return (
    <div className="rounded border border-[var(--fop-border)] bg-[var(--fop-surface)] p-3">
      <div className="font-mono text-[9px] uppercase tracking-wider text-[var(--fop-text-3)] mb-1">{k}</div>
      <div className="text-xs text-[var(--fop-text)] truncate">{v}</div>
    </div>
  );
}

function SelectField({ label, value, options, onChange, testid }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="font-mono text-[10px] uppercase tracking-wider text-[var(--fop-text-3)]">{label}</span>
      <select className="py-2 text-sm" value={value || ""} onChange={e => onChange(e.target.value)} data-testid={testid}>
        {options.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
      </select>
    </label>
  );
}

function TextField({ label, value, onChange, testid, inputMode, multiline, cols }) {
  return (
    <label className={`flex flex-col gap-1.5 ${cols === 2 ? "sm:col-span-2" : ""}`}>
      <span className="font-mono text-[10px] uppercase tracking-wider text-[var(--fop-text-3)]">{label}</span>
      {multiline ? (
        <textarea className="py-2 text-sm" rows={3} value={value || ""} onChange={e => onChange(e.target.value)} data-testid={testid} />
      ) : (
        <input className="py-2 text-sm" value={value ?? ""} inputMode={inputMode} onChange={e => onChange(e.target.value)} data-testid={testid} />
      )}
    </label>
  );
}


function ControlPointsPanel({ projectId, controlPoints, canWrite, onAdd, onImport, onChanged, onEdit, unitsLabel }) {
  const remove = async (id) => {
    if (!window.confirm("Delete this control point?")) return;
    try {
      await api.delete(`/projects/${projectId}/survey-control/control-points/${id}`);
      toast.success("Control point deleted");
      onChanged();
    } catch (e) {
      toast.error("Delete failed: " + (e?.response?.data?.detail || e.message));
    }
  };
  return (
    <div className="fop-surface-2 rounded-lg p-5" data-testid="control-points-panel">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-3)]">Section 2</div>
          <h2 className="font-display text-xl text-[var(--fop-text)]">Control Points <span className="text-sm font-mono text-[var(--fop-text-3)]">({controlPoints.length})</span></h2>
        </div>
        {canWrite && (
          <div className="flex gap-2 flex-wrap">
            <button onClick={onAdd} className="btn btn-primary text-xs inline-flex items-center gap-1.5" data-testid="cp-add-btn">
              <Plus size={12} /> Add Control Point
            </button>
            <button onClick={onImport} className="btn btn-ghost text-xs inline-flex items-center gap-1.5" data-testid="cp-import-btn">
              <Upload size={12} /> Import CSV
            </button>
          </div>
        )}
      </div>
      {controlPoints.length === 0 ? (
        <div className="border border-dashed border-[var(--fop-border-2)] rounded-md p-6 text-center" data-testid="cp-empty-state">
          <MapPin size={24} className="mx-auto text-[var(--fop-text-3)] mb-2" />
          <div className="text-sm text-[var(--fop-text)] mb-1">No control points yet</div>
          <div className="text-xs text-[var(--fop-text-3)] max-w-md mx-auto">
            Type points one at a time, or import a PNEZD CSV from your surveyor.
            Both work. Without control points the platform cannot tie sheets to
            real-world coordinates.
          </div>
          {canWrite && (
            <div className="mt-4 flex justify-center gap-2 flex-wrap">
              <button onClick={onAdd} className="btn btn-primary text-xs inline-flex items-center gap-1.5" data-testid="cp-empty-add-btn">
                <Plus size={12} /> Add Control Point
              </button>
              <button onClick={onImport} className="btn btn-ghost text-xs inline-flex items-center gap-1.5" data-testid="cp-empty-import-btn">
                <Upload size={12} /> Import CSV
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="cp-table">
            <thead>
              <tr className="text-left text-[var(--fop-text-3)] font-mono uppercase tracking-wider text-[9px] border-b border-[var(--fop-border)]">
                <th className="py-2 pr-2">Point #</th>
                <th className="py-2 pr-2">Name</th>
                <th className="py-2 pr-2">Northing ({unitsLabel || "ft"})</th>
                <th className="py-2 pr-2">Easting ({unitsLabel || "ft"})</th>
                <th className="py-2 pr-2">Elevation</th>
                <th className="py-2 pr-2">Description</th>
                <th className="py-2 pr-2">Status</th>
                {canWrite && <th className="py-2 pr-2"></th>}
              </tr>
            </thead>
            <tbody>
              {controlPoints.map(cp => (
                <tr key={cp.id} className="border-b border-[var(--fop-border)] hover:bg-[var(--fop-surface)]"
                    data-testid={`cp-row-${cp.point_number}`}>
                  <td className="py-2 pr-2 font-mono text-[var(--fop-text)]">{cp.point_number}</td>
                  <td className="py-2 pr-2 text-[var(--fop-text-2)]">{cp.point_name || "—"}</td>
                  <td className="py-2 pr-2 font-mono text-[var(--fop-text)]">{cp.northing?.toFixed(3)}</td>
                  <td className="py-2 pr-2 font-mono text-[var(--fop-text)]">{cp.easting?.toFixed(3)}</td>
                  <td className="py-2 pr-2 font-mono text-[var(--fop-text)]">{cp.elevation != null ? cp.elevation.toFixed(3) : "—"}</td>
                  <td className="py-2 pr-2 text-[var(--fop-text-2)] truncate max-w-[200px]">{cp.description || "—"}</td>
                  <td className="py-2 pr-2 font-mono text-[10px] uppercase tracking-wider text-[var(--fop-text-3)]">{cp.status}</td>
                  {canWrite && (
                    <td className="py-2 pr-2">
                      <div className="flex items-center gap-2">
                        <button onClick={() => onEdit(cp)} className="text-[var(--fop-text-3)] hover:text-[var(--fop-primary)]" data-testid={`cp-edit-${cp.point_number}`} title="Edit">
                          <Pencil size={12} />
                        </button>
                        <button onClick={() => remove(cp.id)} className="text-[var(--fop-text-3)] hover:text-[var(--st-escalated)]" data-testid={`cp-delete-${cp.point_number}`} title="Delete">
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function BenchmarksPanel({ projectId, benchmarks, canWrite, onAdd, onChanged, onEdit, unitsLabel }) {
  const remove = async (id) => {
    if (!window.confirm("Delete this benchmark?")) return;
    try {
      await api.delete(`/projects/${projectId}/survey-control/benchmarks/${id}`);
      toast.success("Benchmark deleted");
      onChanged();
    } catch (e) {
      toast.error("Delete failed: " + (e?.response?.data?.detail || e.message));
    }
  };
  return (
    <div className="fop-surface-2 rounded-lg p-5" data-testid="benchmarks-panel">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-3)]">Section 3</div>
          <h2 className="font-display text-xl text-[var(--fop-text)]">Benchmarks <span className="text-sm font-mono text-[var(--fop-text-3)]">({benchmarks.length})</span></h2>
        </div>
        {canWrite && (
          <button onClick={onAdd} className="btn btn-primary text-xs inline-flex items-center gap-1.5" data-testid="bm-add-btn">
            <Plus size={12} /> Add Benchmark
          </button>
        )}
      </div>
      {benchmarks.length === 0 ? (
        <div className="border border-dashed border-[var(--fop-border-2)] rounded-md p-6 text-center" data-testid="bm-empty-state">
          <Ruler size={24} className="mx-auto text-[var(--fop-text-3)] mb-2" />
          <div className="text-sm text-[var(--fop-text)] mb-1">No benchmarks yet</div>
          <div className="text-xs text-[var(--fop-text-3)] max-w-md mx-auto">
            Type the benchmark ID, elevation, and (optionally) the Northing/Easting
            straight from your surveyor&apos;s sheet. At least one verified benchmark
            is required before earthwork or grading starts.
          </div>
          {canWrite && (
            <div className="mt-4 flex justify-center gap-2 flex-wrap">
              <button onClick={onAdd} className="btn btn-primary text-xs inline-flex items-center gap-1.5" data-testid="bm-empty-add-btn">
                <Plus size={12} /> Add Benchmark
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="bm-table">
            <thead>
              <tr className="text-left text-[var(--fop-text-3)] font-mono uppercase tracking-wider text-[9px] border-b border-[var(--fop-border)]">
                <th className="py-2 pr-2">ID</th>
                <th className="py-2 pr-2">Name</th>
                <th className="py-2 pr-2">Elevation</th>
                <th className="py-2 pr-2">Northing ({unitsLabel || "ft"})</th>
                <th className="py-2 pr-2">Easting ({unitsLabel || "ft"})</th>
                <th className="py-2 pr-2">Description</th>
                <th className="py-2 pr-2">Status</th>
                {canWrite && <th className="py-2 pr-2"></th>}
              </tr>
            </thead>
            <tbody>
              {benchmarks.map(b => (
                <tr key={b.id} className="border-b border-[var(--fop-border)] hover:bg-[var(--fop-surface)]"
                    data-testid={`bm-row-${b.benchmark_id}`}>
                  <td className="py-2 pr-2 font-mono text-[var(--fop-text)]">{b.benchmark_id}</td>
                  <td className="py-2 pr-2 text-[var(--fop-text-2)]">{b.benchmark_name || "—"}</td>
                  <td className="py-2 pr-2 font-mono text-[var(--fop-text)]">{b.elevation?.toFixed(3)}</td>
                  <td className="py-2 pr-2 font-mono text-[var(--fop-text-3)]">{b.northing != null ? b.northing.toFixed(3) : "—"}</td>
                  <td className="py-2 pr-2 font-mono text-[var(--fop-text-3)]">{b.easting != null ? b.easting.toFixed(3) : "—"}</td>
                  <td className="py-2 pr-2 text-[var(--fop-text-2)] truncate max-w-[200px]">{b.description || "—"}</td>
                  <td className="py-2 pr-2 font-mono text-[10px] uppercase tracking-wider text-[var(--fop-text-3)]">{b.status}</td>
                  {canWrite && (
                    <td className="py-2 pr-2">
                      <div className="flex items-center gap-2">
                        <button onClick={() => onEdit(b)} className="text-[var(--fop-text-3)] hover:text-[var(--fop-primary)]" data-testid={`bm-edit-${b.benchmark_id}`} title="Edit">
                          <Pencil size={12} />
                        </button>
                        <button onClick={() => remove(b.id)} className="text-[var(--fop-text-3)] hover:text-[var(--st-escalated)]" data-testid={`bm-delete-${b.benchmark_id}`} title="Delete">
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function SidebarGuide({ canWrite }) {
  return (
    <div className="space-y-3 self-start">
      <div className="fop-surface-2 rounded-lg p-4" data-testid="sidebar-guide">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-3)] mb-1">Field Reality</div>
        <h3 className="font-display text-base text-[var(--fop-text)] mb-2">What this does</h3>
        <p className="text-xs text-[var(--fop-text-2)] leading-relaxed">
          Survey Control is how the platform knows where the project is in the real world.
          With a coordinate system + control points + benchmarks + a calibrated sheet, your
          superintendent can stand on the jobsite and see their location on the plans.
        </p>
      </div>
      <div className="fop-surface-2 rounded-lg p-4">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-3)] mb-1">Setup order</div>
        <ol className="text-xs text-[var(--fop-text-2)] space-y-1.5 mt-2 list-decimal pl-4">
          <li>Coordinate system &amp; units (this page)</li>
          <li>Import / add at least 3 control points</li>
          <li>Define at least 1 benchmark</li>
          <li>Calibrate sheets to project coordinates (Calibration Studio per sheet)</li>
          <li>Use &ldquo;Where am I?&rdquo; on the iPad to see your live position</li>
        </ol>
      </div>
      {!canWrite && (
        <div className="fop-surface-2 rounded-lg p-4 border border-[var(--fop-border-2)]">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-3)] mb-1">Read-Only</div>
          <p className="text-xs text-[var(--fop-text-2)] leading-relaxed">
            Only project managers, admins, owners, and executives can edit Survey Control.
            Talk to your PM if any of these values need to change.
          </p>
        </div>
      )}
    </div>
  );
}


// =========================================================
// SETUP WIZARD (auto-opens on first visit)
// =========================================================
function SetupWizard({ settings, catalog, projectId, onClose, onComplete, navigate }) {
  const STEPS = [
    "Coordinate System",
    "Units",
    "Benchmarks",
    "Import Control Points",
    "Calibrate First Sheet",
    "Verify Location Services",
    "Ready for Field Use",
  ];
  const [step, setStep] = useState(0);
  const [draft, setDraft] = useState({ ...settings });
  const [saving, setSaving] = useState(false);

  const setF = (k, v) => setDraft(d => ({ ...d, [k]: v }));

  const saveStep1 = async () => {
    setSaving(true);
    try {
      await api.put(`/projects/${projectId}/survey-control/settings`, {
        horizontal_datum: draft.horizontal_datum,
        coordinate_system_family: draft.coordinate_system_family,
        coordinate_system_zone: ["custom","local_grid"].includes(draft.coordinate_system_family) ? null : draft.coordinate_system_zone,
        vertical_datum: draft.vertical_datum,
        grid_north_mode: draft.grid_north_mode,
        grid_north_rotation_deg: Number(draft.grid_north_rotation_deg || 0),
      });
      setStep(1);
    } catch (e) {
      toast.error("Save failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const saveStep2 = async () => {
    setSaving(true);
    try {
      await api.put(`/projects/${projectId}/survey-control/settings`, {
        horizontal_units: draft.horizontal_units,
        configured: true,
      });
      setStep(2);
    } catch (e) {
      toast.error("Save failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const skipToEnd = () => setStep(STEPS.length - 1);
  const saveDraft = async () => {
    // Persist current values without flipping `configured`.
    try {
      await api.put(`/projects/${projectId}/survey-control/settings`, {
        horizontal_datum: draft.horizontal_datum,
        coordinate_system_family: draft.coordinate_system_family,
        coordinate_system_zone: ["custom","local_grid"].includes(draft.coordinate_system_family) ? null : draft.coordinate_system_zone,
        vertical_datum: draft.vertical_datum,
        horizontal_units: draft.horizontal_units,
        grid_north_mode: draft.grid_north_mode,
        grid_north_rotation_deg: Number(draft.grid_north_rotation_deg || 0),
      });
      toast.success("Draft saved — resume anytime");
      onClose();
    } catch (e) {
      toast.error("Save failed: " + (e?.response?.data?.detail || e.message));
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="survey-wizard">
      <div className="bg-[var(--fop-bg)] border border-[var(--fop-border)] rounded-lg w-full max-w-3xl max-h-[90vh] overflow-auto">
        <div className="p-5 border-b border-[var(--fop-border)] flex items-center justify-between gap-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-3)]">Survey Control · Setup Wizard</div>
            <div className="font-display text-lg text-[var(--fop-text)] mt-0.5">Step {step + 1} of {STEPS.length}: {STEPS[step]}</div>
          </div>
          <button onClick={onClose} className="text-[var(--fop-text-3)] hover:text-[var(--fop-text)]" data-testid="wizard-close">
            <X size={18} />
          </button>
        </div>

        <div className="flex flex-wrap gap-1.5 px-5 pt-4" data-testid="wizard-steps">
          {STEPS.map((s, i) => (
            <div key={s}
                  className={`shrink-0 flex items-center justify-center rounded-full font-mono text-[10px] font-bold w-6 h-6 sm:w-auto sm:h-auto sm:rounded sm:px-2 sm:py-1 sm:uppercase sm:tracking-wider ${
                    i < step ? "bg-[rgba(34,197,94,0.15)] text-[rgb(34,197,94)]"
                    : i === step ? "bg-[var(--fop-primary-soft)] text-[var(--fop-primary)]"
                    : "bg-[var(--fop-surface)] text-[var(--fop-text-3)]"
                  }`}
                  title={s}
                  data-testid={`wizard-step-pill-${i}`}>
              <span className="sm:hidden">{i + 1}</span>
              <span className="hidden sm:inline">{i + 1}. {s}</span>
            </div>
          ))}
        </div>

        <div className="p-5">
          {step === 0 && (
            <div className="space-y-3" data-testid="wizard-step-0">
              <p className="text-sm text-[var(--fop-text-2)]">
                Choose how this project ties to the real world. Defaults are pre-filled for US
                heavy-civil work — adjust if your surveyor&apos;s control sheet says otherwise.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <SelectField label="Horizontal Datum" value={draft.horizontal_datum} options={catalog.horizontal_datums} onChange={v => setF("horizontal_datum", v)} testid="wiz-horizontal-datum" />
                <SelectField label="Coordinate System Family" value={draft.coordinate_system_family} options={catalog.coordinate_system_families} onChange={v => setF("coordinate_system_family", v)} testid="wiz-cs-family" />
                {!["custom","local_grid"].includes(draft.coordinate_system_family) && (
                  <SelectField label="Zone" value={draft.coordinate_system_zone || ""} options={catalog.coordinate_system_zones} onChange={v => setF("coordinate_system_zone", v)} testid="wiz-cs-zone" />
                )}
                <SelectField label="Vertical Datum" value={draft.vertical_datum} options={catalog.vertical_datums} onChange={v => setF("vertical_datum", v)} testid="wiz-vertical-datum" />
                <SelectField label="Grid North" value={draft.grid_north_mode} options={catalog.grid_north_modes} onChange={v => setF("grid_north_mode", v)} testid="wiz-grid-north" />
                <TextField label="Rotation (degrees)" inputMode="decimal" value={draft.grid_north_rotation_deg ?? 0} onChange={v => setF("grid_north_rotation_deg", v)} testid="wiz-rotation" />
              </div>
            </div>
          )}
          {step === 1 && (
            <div className="space-y-3" data-testid="wizard-step-1">
              <p className="text-sm text-[var(--fop-text-2)]">
                What units does your survey work in? Heavy-civil US projects almost always use US Survey Feet.
              </p>
              <SelectField label="Horizontal Units" value={draft.horizontal_units} options={catalog.horizontal_units} onChange={v => setF("horizontal_units", v)} testid="wiz-units" />
            </div>
          )}
          {step === 2 && (
            <div className="space-y-3" data-testid="wizard-step-2">
              <p className="text-sm text-[var(--fop-text-2)]">
                Benchmarks are the elevation reference points for your project. Most jobs need at
                least one verified benchmark before earthwork or grading starts.
              </p>
              <p className="text-xs text-[var(--fop-text-3)]">
                You can add benchmarks in the next section after the wizard closes — or skip
                and come back later.
              </p>
            </div>
          )}
          {step === 3 && (
            <div className="space-y-3" data-testid="wizard-step-3">
              <p className="text-sm text-[var(--fop-text-2)]">
                Most surveyors deliver a CSV with point number, northing, easting, elevation, and
                description (PNEZD format). You can import that file directly after the wizard
                closes.
              </p>
              <p className="text-xs text-[var(--fop-text-3)]">
                For now we&apos;ll save your draft and you can import the CSV from the Control Points
                section.
              </p>
            </div>
          )}
          {step === 4 && (
            <div className="space-y-3" data-testid="wizard-step-4">
              <p className="text-sm text-[var(--fop-text-2)]">
                Sheet calibration ties each plan sheet to your project coordinates so the iPad
                viewer can map your GPS position onto the drawing.
              </p>
              <p className="text-xs text-[var(--fop-text-3)] inline-flex items-center gap-1.5">
                <CheckCircle2 size={12} className="text-[rgb(34,197,94)]" />
                Open any sheet → More menu → Project calibration to launch the Calibration Studio.
              </p>
            </div>
          )}
          {step === 5 && (
            <div className="space-y-3" data-testid="wizard-step-5">
              <p className="text-sm text-[var(--fop-text-2)]">
                Your iPad needs Location Services enabled and granted to the ForgedOps Plans web
                app. We will verify on-device when you open a calibrated sheet.
              </p>
              <p className="text-xs text-[var(--fop-text-3)] inline-flex items-center gap-1.5">
                <CheckCircle2 size={12} className="text-[rgb(34,197,94)]" />
                On the iPad, tap &ldquo;Where am I?&rdquo; (top-right of any sheet) to see your project position.
              </p>
            </div>
          )}
          {step === 6 && (
            <div className="space-y-3 text-center py-4" data-testid="wizard-step-6">
              <CheckCircle2 size={48} className="text-[rgb(34,197,94)] mx-auto" />
              <h3 className="font-display text-xl text-[var(--fop-text)]">Survey Control draft saved</h3>
              <p className="text-sm text-[var(--fop-text-2)] max-w-md mx-auto">
                Add control points, define benchmarks, and calibrate sheets to flip the dashboard
                indicator to GREEN.
              </p>
            </div>
          )}
        </div>

        <div className="p-5 border-t border-[var(--fop-border)] flex items-center justify-between gap-2 flex-wrap">
          <div className="flex gap-2">
            <button onClick={saveDraft} className="btn btn-ghost text-xs" data-testid="wizard-save-draft">Save draft</button>
            <button onClick={skipToEnd} className="btn btn-ghost text-xs" data-testid="wizard-skip">Skip</button>
          </div>
          <div className="flex gap-2">
            {step > 0 && (
              <button onClick={() => setStep(s => s - 1)} className="btn btn-ghost text-sm inline-flex items-center gap-1" data-testid="wizard-back">
                <ChevronLeft size={14} /> Back
              </button>
            )}
            {step === 0 && (
              <button onClick={saveStep1} disabled={saving} className="btn btn-primary text-sm inline-flex items-center gap-1" data-testid="wizard-next">
                Save & Continue <ChevronRight size={14} />
              </button>
            )}
            {step === 1 && (
              <button onClick={saveStep2} disabled={saving} className="btn btn-primary text-sm inline-flex items-center gap-1" data-testid="wizard-next">
                Save & Continue <ChevronRight size={14} />
              </button>
            )}
            {step > 1 && step < STEPS.length - 1 && (
              <button onClick={() => setStep(s => s + 1)} className="btn btn-primary text-sm inline-flex items-center gap-1" data-testid="wizard-next">
                Next <ChevronRight size={14} />
              </button>
            )}
            {step === STEPS.length - 1 && (
              <button onClick={onComplete} className="btn btn-primary text-sm" data-testid="wizard-finish">
                Finish
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


// =========================================================
// NEW / EDIT CONTROL POINT MODAL
// =========================================================
function NewControlPointModal({ projectId, onClose, onSaved, unitsLabel, initial }) {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    point_number: initial?.point_number || "",
    point_name: initial?.point_name || "",
    northing: initial?.northing != null ? String(initial.northing) : "",
    easting: initial?.easting != null ? String(initial.easting) : "",
    elevation: initial?.elevation != null ? String(initial.elevation) : "",
    description: initial?.description || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = async () => {
    if (!form.point_number || form.northing === "" || form.easting === "") {
      toast.error("Point number, northing, and easting are required");
      return;
    }
    if (!Number.isFinite(Number(form.northing)) || !Number.isFinite(Number(form.easting))) {
      toast.error("Northing and Easting must be numbers");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        point_number: form.point_number,
        point_name: form.point_name || null,
        northing: Number(form.northing),
        easting: Number(form.easting),
        elevation: form.elevation === "" ? null : Number(form.elevation),
        description: form.description || null,
      };
      if (isEdit) {
        await api.patch(`/projects/${projectId}/survey-control/control-points/${initial.id}`, payload);
        toast.success("Control point updated");
      } else {
        await api.post(`/projects/${projectId}/survey-control/control-points`, payload);
        toast.success("Control point added");
      }
      onSaved();
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      const status = e?.response?.status;
      if (status === 409) {
        toast.error(detail || "Duplicate point number");
      } else {
        toast.error("Save failed: " + detail);
      }
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="new-cp-modal">
      <div className="bg-[var(--fop-bg)] border border-[var(--fop-border)] rounded-lg w-full max-w-xl">
        <div className="p-5 border-b border-[var(--fop-border)] flex items-center justify-between">
          <h3 className="font-display text-lg text-[var(--fop-text)]" data-testid="cp-modal-title">
            {isEdit ? "Edit Control Point" : "Add Control Point"}
          </h3>
          <button onClick={onClose} className="text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"><X size={18} /></button>
        </div>
        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <TextField label="Point Number *" value={form.point_number} onChange={v => set("point_number", v)} testid="cp-field-number" />
          <TextField label="Point Name" value={form.point_name} onChange={v => set("point_name", v)} testid="cp-field-name" />
          <TextField label={`Northing (${unitsLabel || "ft"}) *`} value={form.northing} inputMode="decimal" onChange={v => set("northing", v)} testid="cp-field-northing" />
          <TextField label={`Easting (${unitsLabel || "ft"}) *`} value={form.easting} inputMode="decimal" onChange={v => set("easting", v)} testid="cp-field-easting" />
          <TextField label="Elevation" value={form.elevation} inputMode="decimal" onChange={v => set("elevation", v)} testid="cp-field-elevation" />
          <TextField label="Description" value={form.description} onChange={v => set("description", v)} testid="cp-field-desc" cols={2} />
        </div>
        <div className="p-5 border-t border-[var(--fop-border)] flex justify-end gap-2">
          <button onClick={onClose} className="btn btn-ghost text-sm" data-testid="cp-modal-cancel">Cancel</button>
          <button onClick={save} disabled={saving} className="btn btn-primary text-sm" data-testid="cp-modal-save">
            {saving ? "Saving…" : (isEdit ? "Update" : "Save")}
          </button>
        </div>
      </div>
    </div>
  );
}


// =========================================================
// NEW / EDIT BENCHMARK MODAL
// =========================================================
function NewBenchmarkModal({ projectId, onClose, onSaved, unitsLabel, initial }) {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    benchmark_id: initial?.benchmark_id || "",
    benchmark_name: initial?.benchmark_name || "",
    elevation: initial?.elevation != null ? String(initial.elevation) : "",
    northing: initial?.northing != null ? String(initial.northing) : "",
    easting: initial?.easting != null ? String(initial.easting) : "",
    description: initial?.description || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = async () => {
    if (!form.benchmark_id || form.elevation === "") {
      toast.error("Benchmark ID and elevation are required");
      return;
    }
    if (!Number.isFinite(Number(form.elevation))) {
      toast.error("Elevation must be a number");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        benchmark_id: form.benchmark_id,
        benchmark_name: form.benchmark_name || null,
        elevation: Number(form.elevation),
        northing: form.northing === "" ? null : Number(form.northing),
        easting: form.easting === "" ? null : Number(form.easting),
        description: form.description || null,
      };
      if (isEdit) {
        await api.patch(`/projects/${projectId}/survey-control/benchmarks/${initial.id}`, payload);
        toast.success("Benchmark updated");
      } else {
        await api.post(`/projects/${projectId}/survey-control/benchmarks`, payload);
        toast.success("Benchmark added");
      }
      onSaved();
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      const status = e?.response?.status;
      if (status === 409) {
        toast.error(detail || "Duplicate benchmark ID");
      } else {
        toast.error("Save failed: " + detail);
      }
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="new-bm-modal">
      <div className="bg-[var(--fop-bg)] border border-[var(--fop-border)] rounded-lg w-full max-w-xl">
        <div className="p-5 border-b border-[var(--fop-border)] flex items-center justify-between">
          <h3 className="font-display text-lg text-[var(--fop-text)]" data-testid="bm-modal-title">
            {isEdit ? "Edit Benchmark" : "Add Benchmark"}
          </h3>
          <button onClick={onClose} className="text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"><X size={18} /></button>
        </div>
        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <TextField label="Benchmark ID *" value={form.benchmark_id} onChange={v => set("benchmark_id", v)} testid="bm-field-id" />
          <TextField label="Benchmark Name" value={form.benchmark_name} onChange={v => set("benchmark_name", v)} testid="bm-field-name" />
          <TextField label={`Elevation (${unitsLabel || "ft"}) *`} value={form.elevation} inputMode="decimal" onChange={v => set("elevation", v)} testid="bm-field-elevation" />
          <TextField label={`Northing (${unitsLabel || "ft"})`} value={form.northing} inputMode="decimal" onChange={v => set("northing", v)} testid="bm-field-northing" />
          <TextField label={`Easting (${unitsLabel || "ft"})`} value={form.easting} inputMode="decimal" onChange={v => set("easting", v)} testid="bm-field-easting" />
          <TextField label="Description" value={form.description} onChange={v => set("description", v)} testid="bm-field-desc" cols={2} />
        </div>
        <div className="p-5 border-t border-[var(--fop-border)] flex justify-end gap-2">
          <button onClick={onClose} className="btn btn-ghost text-sm" data-testid="bm-modal-cancel">Cancel</button>
          <button onClick={save} disabled={saving} className="btn btn-primary text-sm" data-testid="bm-modal-save">
            {saving ? "Saving…" : (isEdit ? "Update" : "Save")}
          </button>
        </div>
      </div>
    </div>
  );
}


// =========================================================
// CSV IMPORT MODAL (PNEZD preview + commit)
// =========================================================
function ImportCSVModal({ projectId, onClose, onCommitted }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);

  const upload = async (f) => {
    setFile(f); setPreview(null); setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(
        `/projects/${projectId}/survey-control/control-points/import/preview`,
        fd, { headers: { "Content-Type": "multipart/form-data" } },
      );
      setPreview(data);
    } catch (e) {
      toast.error("Preview failed: " + (e?.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };

  const toggleRow = (rowNum) => {
    setPreview(p => ({
      ...p,
      rows: p.rows.map(r => r.row_number === rowNum ? { ...r, will_import: !r.will_import } : r),
    }));
  };

  const commit = async () => {
    if (!preview) return;
    setCommitting(true);
    try {
      const { data } = await api.post(
        `/projects/${projectId}/survey-control/control-points/import/commit`,
        { rows: preview.rows },
      );
      toast.success(`Imported ${data.inserted} control points (${data.skipped} skipped)`);
      onCommitted();
    } catch (e) {
      toast.error("Import failed: " + (e?.response?.data?.detail || e.message));
    } finally { setCommitting(false); }
  };

  const importableCount = useMemo(
    () => preview ? preview.rows.filter(r => r.will_import && !r.error).length : 0,
    [preview]
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="import-csv-modal">
      <div className="bg-[var(--fop-bg)] border border-[var(--fop-border)] rounded-lg w-full max-w-4xl max-h-[90vh] overflow-auto">
        <div className="p-5 border-b border-[var(--fop-border)] flex items-center justify-between">
          <div>
            <h3 className="font-display text-lg text-[var(--fop-text)]">Import Control Points</h3>
            <div className="text-xs text-[var(--fop-text-3)]">PNEZD CSV (Point, Northing, Easting, Elevation, Description)</div>
          </div>
          <button onClick={onClose} className="text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"><X size={18} /></button>
        </div>

        <div className="p-5">
          {!preview && (
            <label className="block border-2 border-dashed border-[var(--fop-border-2)] rounded-lg p-8 text-center cursor-pointer hover:border-[var(--fop-primary)]" data-testid="csv-dropzone">
              <FileText size={32} className="mx-auto text-[var(--fop-text-3)] mb-2" />
              <div className="text-sm text-[var(--fop-text)] mb-1">
                {loading ? "Parsing…" : "Click to choose a CSV file"}
              </div>
              <div className="text-xs text-[var(--fop-text-3)]">
                Accepts PNEZD, PENZD, or NEZA column orders. Max 5MB.
              </div>
              <input type="file" accept=".csv,text/csv"
                      className="hidden"
                      onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}
                      data-testid="csv-file-input" />
            </label>
          )}

          {preview && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-3 text-xs">
                <span className="font-mono uppercase tracking-wider text-[var(--fop-text-3)]">Detected format:</span>
                <span className="font-mono text-[var(--fop-text)]" data-testid="detected-format">{preview.detected_format.toUpperCase()}</span>
                <span className="font-mono uppercase tracking-wider text-[var(--fop-text-3)]">Rows:</span>
                <span className="font-mono text-[var(--fop-text)]" data-testid="total-rows">{preview.total_rows}</span>
                {preview.errors_count > 0 && (
                  <span className="font-mono text-[rgb(239,68,68)] inline-flex items-center gap-1" data-testid="errors-count">
                    <AlertTriangle size={12} /> {preview.errors_count} errors
                  </span>
                )}
                <span className="ml-auto text-[var(--fop-text-2)]">
                  <span data-testid="importable-count">{importableCount}</span> will be imported
                </span>
              </div>

              <div className="overflow-x-auto max-h-[50vh] overflow-y-auto border border-[var(--fop-border)] rounded">
                <table className="w-full text-xs" data-testid="csv-preview-table">
                  <thead className="sticky top-0 bg-[var(--fop-surface-2)]">
                    <tr className="text-left text-[var(--fop-text-3)] font-mono uppercase tracking-wider text-[9px] border-b border-[var(--fop-border)]">
                      <th className="py-2 px-2">Use</th>
                      <th className="py-2 px-2">Row</th>
                      <th className="py-2 px-2">Pt #</th>
                      <th className="py-2 px-2">Northing</th>
                      <th className="py-2 px-2">Easting</th>
                      <th className="py-2 px-2">Elevation</th>
                      <th className="py-2 px-2">Description</th>
                      <th className="py-2 px-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map(r => (
                      <tr key={r.row_number}
                          className={`border-b border-[var(--fop-border)] ${r.error ? "bg-[rgba(239,68,68,0.05)]" : ""}`}
                          data-testid={`csv-row-${r.row_number}`}>
                        <td className="py-1.5 px-2">
                          <input type="checkbox" disabled={!!r.error}
                                  checked={!!r.will_import && !r.error}
                                  onChange={() => toggleRow(r.row_number)}
                                  data-testid={`csv-row-${r.row_number}-checkbox`} />
                        </td>
                        <td className="py-1.5 px-2 font-mono text-[var(--fop-text-3)]">{r.row_number}</td>
                        <td className="py-1.5 px-2 font-mono">{r.point_number || "—"}</td>
                        <td className="py-1.5 px-2 font-mono">{r.northing ?? "—"}</td>
                        <td className="py-1.5 px-2 font-mono">{r.easting ?? "—"}</td>
                        <td className="py-1.5 px-2 font-mono">{r.elevation ?? "—"}</td>
                        <td className="py-1.5 px-2 truncate max-w-[200px]">{r.description || "—"}</td>
                        <td className="py-1.5 px-2">
                          {r.error
                            ? <span className="text-[rgb(239,68,68)] text-[11px]">{r.error}</span>
                            : <span className="text-[rgb(34,197,94)] text-[11px]">OK</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="p-5 border-t border-[var(--fop-border)] flex items-center justify-between gap-2">
          <div className="text-xs text-[var(--fop-text-3)]">
            {file ? file.name : "No file chosen"}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="btn btn-ghost text-sm" data-testid="import-cancel">Cancel</button>
            {preview && (
              <button onClick={commit} disabled={committing || importableCount === 0}
                      className="btn btn-primary text-sm inline-flex items-center gap-1.5"
                      data-testid="import-commit-btn">
                <Upload size={14} /> {committing ? "Importing…" : `Import ${importableCount} points`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}



// ============================================================
// SC-3 — Sheet Calibrations Panel (roll-up + per-sheet calibrate link)
// ============================================================
const SC_BADGE = {
  not_calibrated: { bg: "rgba(239,68,68,0.10)",  text: "rgb(239,68,68)",  label: "Not Calibrated" },
  partial:        { bg: "rgba(245,158,11,0.10)", text: "rgb(245,158,11)", label: "Partial" },
  calibrated:     { bg: "rgba(34,197,94,0.10)",  text: "rgb(34,197,94)",  label: "Calibrated" },
};

function SheetCalibrationsPanel({ projectId, unitsLabel }) {
  const [rows, setRows] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/projects/${projectId}/survey-control/sheet-calibrations`);
        if (!cancelled) setRows(r.data || []);
      } catch {
        if (!cancelled) setRows([]);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  if (rows === null) {
    return (
      <div className="fop-surface-2 rounded-lg p-5" data-testid="sheet-calibrations-loading">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-3)]">Loading sheet calibrations…</div>
      </div>
    );
  }
  const total = rows.length;
  const calibrated = rows.filter(r => r.status === "calibrated").length;
  const partial = rows.filter(r => r.status === "partial").length;
  return (
    <div className="fop-surface-2 rounded-lg p-5" data-testid="sheet-calibrations-panel">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-3)]">Section 4</div>
          <h2 className="font-display text-xl text-[var(--fop-text)]">
            Sheet Calibrations <span className="text-sm font-mono text-[var(--fop-text-3)]">({calibrated}/{total})</span>
          </h2>
        </div>
        <div className="text-xs text-[var(--fop-text-2)] inline-flex items-center gap-3">
          <span><span className="text-[rgb(34,197,94)] font-mono">{calibrated}</span> calibrated</span>
          <span><span className="text-[rgb(245,158,11)] font-mono">{partial}</span> partial</span>
          <span><span className="text-[rgb(239,68,68)] font-mono">{total - calibrated - partial}</span> not calibrated</span>
        </div>
      </div>
      {total === 0 ? (
        <div className="border border-dashed border-[var(--fop-border-2)] rounded-md p-6 text-center" data-testid="sheet-cal-empty">
          <Layers size={24} className="mx-auto text-[var(--fop-text-3)] mb-2" />
          <div className="text-sm text-[var(--fop-text)]">No sheets in this project yet</div>
          <div className="text-xs text-[var(--fop-text-3)] mt-1">Upload a plan set, then calibrate each sheet to project coordinates.</div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="sheet-cal-table">
            <thead>
              <tr className="text-left text-[var(--fop-text-3)] font-mono uppercase tracking-wider text-[9px] border-b border-[var(--fop-border)]">
                <th className="py-2 pr-2">Sheet</th>
                <th className="py-2 pr-2">Title</th>
                <th className="py-2 pr-2">Status</th>
                <th className="py-2 pr-2">Confidence</th>
                <th className="py-2 pr-2">RMS ({unitsLabel || "ft"})</th>
                <th className="py-2 pr-2">Pairs</th>
                <th className="py-2 pr-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const badge = SC_BADGE[r.status] || SC_BADGE.not_calibrated;
                return (
                  <tr key={r.sheet_id} className="border-b border-[var(--fop-border)] hover:bg-[var(--fop-surface)]"
                      data-testid={`sheet-cal-row-${r.sheet_number}`}>
                    <td className="py-2 pr-2 font-mono text-[var(--fop-text)]">{r.sheet_number}</td>
                    <td className="py-2 pr-2 text-[var(--fop-text-2)] truncate max-w-[260px]">{r.sheet_title}</td>
                    <td className="py-2 pr-2">
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider"
                              style={{ background: badge.bg, color: badge.text }}>
                        {badge.label}
                      </span>
                    </td>
                    <td className="py-2 pr-2 font-mono text-[10px] uppercase tracking-wider text-[var(--fop-text-2)]">
                      {r.confidence || "—"}
                    </td>
                    <td className="py-2 pr-2 font-mono text-[var(--fop-text)]">
                      {r.rms_residual != null ? r.rms_residual.toFixed(3) : "—"}
                    </td>
                    <td className="py-2 pr-2 font-mono text-[var(--fop-text-2)]">{r.pairs_count}</td>
                    <td className="py-2 pr-2">
                      <Link to={`/sheet/${r.sheet_id}/calibrate`} className="btn btn-ghost text-[11px] inline-flex items-center gap-1"
                            data-testid={`sheet-cal-open-${r.sheet_number}`}>
                        <Crosshair size={11} /> Calibrate
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


/* ============================================================
 * Iter EXP-1 — READINESS VERDICT CARD
 *
 * Single source of truth for "is this project field-ready?".
 * Replaces the operator's confusion across 8 sections with one big
 * answer and the missing items linked.
 * ============================================================ */
function ReadinessVerdict({ status, settings, controlPoints, benchmarks, canWrite, onOpenWizard }) {
  const items = [];
  if (!settings?.configured) {
    items.push({ key: "settings", label: "Project coordinate system not set",
                 ctaLabel: "Open Setup Wizard",
                 onClick: canWrite ? onOpenWizard : null });
  }
  if (!controlPoints || controlPoints.length === 0) {
    items.push({ key: "control", label: "No control points entered",
                 ctaLabel: "Add control point",
                 onClick: canWrite ? () => {
                   const el = document.querySelector(`[data-testid="control-points-panel"]`);
                   if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                 } : null });
  }
  if (!benchmarks || benchmarks.length === 0) {
    items.push({ key: "benchmarks", label: "No benchmarks defined",
                 ctaLabel: "Add benchmark",
                 onClick: canWrite ? () => {
                   const el = document.querySelector(`[data-testid="benchmarks-panel"]`);
                   if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                 } : null });
  }
  const ready = items.length === 0 && (status?.overall_state === "green" || status?.overall_state === "yellow");

  return (
    <div className="mb-5" data-testid="readiness-verdict-wrap">
      <ReadinessBanner
        state={ready ? "ready" : "needs-action"}
        title={ready
          ? "Project is field-ready"
          : `${items.length} item${items.length === 1 ? "" : "s"} before field work`}
        subtitle={ready
          ? "Coordinate system set, control points entered, benchmarks verified. Crews can measure, locate, and mark with confidence."
          : "Field crews need a complete coordinate system, control points, and at least one verified benchmark to trust the platform."}
        items={items}
        testid="readiness-verdict"
      />
    </div>
  );
}
